import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { LoadingSpinner, ErrorBanner, EmptyState } from '../components/AdminStatus';

const API   = 'https://api.udanchu.com';
const font  = "'DM Sans', system-ui, sans-serif";
const LIMIT = 20;

const getKey = () => sessionStorage.getItem('udanchu_admin_key') || '';

const DOMAINS = ['all', 'traffic', 'consumer', 'police'];

const DOMAIN_COLORS = {
  traffic:  { bg: 'rgba(79,142,247,0.15)',  color: '#4f8ef7' },
  consumer: { bg: 'rgba(52,211,153,0.15)',  color: '#34d399' },
  police:   { bg: 'rgba(251,191,36,0.15)',  color: '#fbbf24' },
};
const domainStyle = d => DOMAIN_COLORS[d] || { bg: 'rgba(100,116,139,0.15)', color: '#64748b' };

const STATUS_STYLES = {
  active:     { bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.3)',  color: '#34d399' },
  draft:      { bg: 'rgba(251,191,36,0.12)',  border: 'rgba(251,191,36,0.3)',  color: '#fbbf24' },
  deprecated: { bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.3)', color: '#f87171' },
  inactive:   { bg: 'rgba(248,113,113,0.12)', border: 'rgba(248,113,113,0.3)', color: '#f87171' },
};
const statusStyle = s => STATUS_STYLES[s] || STATUS_STYLES.inactive;

const s = {
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '18px',
    flexWrap: 'wrap',
  },
  select: {
    background: '#181c27',
    border: '1px solid #252a38',
    borderRadius: '7px',
    color: '#e2e8f0',
    fontFamily: font,
    fontSize: '13px',
    padding: '7px 12px',
    outline: 'none',
    cursor: 'pointer',
  },
  search: {
    flex: 1,
    minWidth: '180px',
    background: '#181c27',
    border: '1px solid #252a38',
    borderRadius: '7px',
    color: '#e2e8f0',
    fontFamily: font,
    fontSize: '13px',
    padding: '7px 13px',
    outline: 'none',
  },
  applyBtn: {
    background: '#4f8ef7',
    border: 'none',
    borderRadius: '7px',
    color: '#fff',
    fontFamily: font,
    fontSize: '13px',
    fontWeight: '600',
    padding: '7px 18px',
    cursor: 'pointer',
    flexShrink: 0,
  },
  newCardBtn: {
    background: 'rgba(52,211,153,0.12)',
    border: '1px solid rgba(52,211,153,0.3)',
    borderRadius: '7px',
    color: '#34d399',
    fontFamily: font,
    fontSize: '13px',
    fontWeight: '600',
    padding: '7px 16px',
    cursor: 'pointer',
    flexShrink: 0,
    marginLeft: 'auto',
  },
  count: {
    color: '#64748b',
    fontFamily: font,
    fontSize: '13px',
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: font,
  },
  th: {
    background: '#181c27',
    borderBottom: '1px solid #252a38',
    color: '#64748b',
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.07em',
    padding: '10px 14px',
    textAlign: 'left',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  td: {
    borderBottom: '1px solid #1a1f2e',
    color: '#e2e8f0',
    fontSize: '13px',
    padding: '12px 14px',
    verticalAlign: 'middle',
  },
  domainBadge: {
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '0.06em',
    padding: '2px 7px',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  statusBadge: {
    borderRadius: '4px',
    fontSize: '10px',
    fontWeight: '600',
    letterSpacing: '0.04em',
    padding: '2px 7px',
    border: '1px solid',
    whiteSpace: 'nowrap',
  },
  titleText: {
    color: '#e2e8f0',
    fontWeight: '500',
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '260px',
  },
  lawRef: {
    color: '#64748b',
    fontSize: '12px',
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  actionsCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  viewBtn: {
    background: 'transparent',
    border: '1px solid #252a38',
    borderRadius: '6px',
    color: '#64748b',
    fontFamily: font,
    fontSize: '12px',
    fontWeight: '500',
    padding: '4px 10px',
    textDecoration: 'none',
    display: 'inline-block',
    whiteSpace: 'nowrap',
  },
  editBtn: {
    background: 'rgba(79,142,247,0.1)',
    border: '1px solid rgba(79,142,247,0.3)',
    borderRadius: '6px',
    color: '#4f8ef7',
    fontFamily: font,
    fontSize: '12px',
    fontWeight: '500',
    padding: '4px 10px',
    textDecoration: 'none',
    display: 'inline-block',
    whiteSpace: 'nowrap',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    marginTop: '20px',
    fontFamily: font,
  },
  pageBtn: {
    background: 'transparent',
    border: '1px solid #252a38',
    borderRadius: '6px',
    color: '#64748b',
    fontFamily: font,
    fontSize: '13px',
    fontWeight: '500',
    padding: '6px 14px',
    cursor: 'pointer',
  },
  pageBtnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  pageInfo: { color: '#64748b', fontSize: '13px' },
};

function CardRow({ card }) {
  const ds  = domainStyle(card.domain);
  const ss  = statusStyle(card.status);
  const ref = card.law_references?.[0];
  const lawText = ref
    ? `${ref.act}${ref.section ? ` § ${ref.section}` : ''}`
    : '—';

  return (
    <tr>
      <td style={s.td}>
        <span style={s.titleText} title={card.title}>{card.title}</span>
        <span style={{ ...s.titleText, color: '#64748b', fontSize: '11px', fontWeight: '400', marginTop: '2px' }}>
          {card.id}
        </span>
      </td>
      <td style={s.td}>
        <span style={{ ...s.domainBadge, background: ds.bg, color: ds.color }}>
          {card.domain || 'general'}
        </span>
      </td>
      <td style={s.td}>
        <span style={{ ...s.statusBadge, background: ss.bg, borderColor: ss.border, color: ss.color }}>
          {card.status}
        </span>
      </td>
      <td style={s.td}>
        <span style={s.lawRef} title={lawText}>{lawText}</span>
      </td>
      <td style={s.td}>
        <div style={s.actionsCell}>
          <Link to={`/admin/card/${card.id}`}      style={s.viewBtn}>View</Link>
          <Link to={`/admin/card/${card.id}/edit`} style={s.editBtn}>Edit</Link>
        </div>
      </td>
    </tr>
  );
}

export default function AdminCards() {
  const navigate = useNavigate();

  const [domain,  setDomain]  = useState('all');
  const [search,  setSearch]  = useState('');
  const [applied, setApplied] = useState({ domain: 'all', search: '' });
  const [page,    setPage]    = useState(1);
  const [cards,   setCards]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ page, limit: LIMIT });
    if (applied.domain !== 'all') params.set('domain', applied.domain);
    if (applied.search.trim())    params.set('search', applied.search.trim());

    fetch(`${API}/cards?${params}`, { headers: { 'X-Admin-Key': getKey() } })
      .then(r => r.ok ? r.json() : r.json().then(j => Promise.reject(j.message || `Error ${r.status}`)))
      .then(data => { if (!cancelled) { setCards(data.cards || []); setTotal(data.total || 0); } })
      .catch(err  => { if (!cancelled) setError(typeof err === 'string' ? err : 'Failed to load cards.'); })
      .finally(()  => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [applied, page]);

  const handleApply   = () => { setPage(1); setApplied({ domain, search }); };
  const handleKeyDown = e  => { if (e.key === 'Enter') handleApply(); };
  const totalPages    = Math.ceil(total / LIMIT);

  return (
    <AdminLayout title="Live Cards">
      <div style={s.toolbar}>
        <select style={s.select} value={domain} onChange={e => setDomain(e.target.value)}>
          {DOMAINS.map(d => (
            <option key={d} value={d}>{d === 'all' ? 'All domains' : d}</option>
          ))}
        </select>

        <input
          style={s.search}
          type="text"
          placeholder="Search cards..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
        />

        <button style={s.applyBtn} onClick={handleApply}>Apply</button>

        {!loading && !error && (
          <span style={s.count}>{total} card{total !== 1 ? 's' : ''}</span>
        )}

        <button style={s.newCardBtn} onClick={() => navigate('/admin/card/new/edit')}>
          + New Card
        </button>
      </div>

      {loading && <LoadingSpinner message="Loading cards..." />}

      {!loading && error && (
        <ErrorBanner error={error} onRetry={() => setApplied(a => ({ ...a }))} />
      )}

      {!loading && !error && cards.length === 0 && (
        <EmptyState message="No cards match your search." />
      )}

      {!loading && !error && cards.length > 0 && (
        <>
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Title</th>
                  <th style={s.th}>Domain</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Law Reference</th>
                  <th style={s.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {cards.map(card => <CardRow key={card.id} card={card} />)}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={s.pagination}>
              <button
                style={{ ...s.pageBtn, ...(page <= 1 ? s.pageBtnDisabled : {}) }}
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >← Prev</button>
              <span style={s.pageInfo}>Page {page} of {totalPages}</span>
              <button
                style={{ ...s.pageBtn, ...(page >= totalPages ? s.pageBtnDisabled : {}) }}
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >Next →</button>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}
